import React from 'react'
import { noop } from 'lodash-es'
import { Field, Focusable } from '../inputs'
import { InputContainer } from '../layout'
import { TextButtonType } from '../buttons'
import { Combobox, ComboboxProps } from './Combobox'
import { ListboxOptions, OptionKey, OptionTemplateProps } from './Listbox'
import {
  DropdownOption,
  SelectButton,
  SelectedOption,
  RemoveItemButton,
  MultiComboboxWrapper,
  SelectedListbox,
  ClearButton
} from './optionsStyles'
import { Locale, Translation } from 'locale'
import { iconSet } from '../icons'
import styled from '@emotion/styled'

export interface MultiComboboxProps<T, K extends OptionKey> extends ListboxOptions<T, K> {
  id?: string
  withPortal?: boolean
  className?: string
  placeholder?: string
  value: K[]
  readOnly?: boolean
  disabled?: boolean
  canClear?: boolean
  clearButtonLabel?: string
  selectedOptionCustomComponent?: (optionKey: K) => JSX.Element
  onUpdate: (optionKey: K[], ...rest: any[]) => void
  dataTestId?: string
  innerComboboxProps?: Partial<ComboboxProps<T, K>>
  showSelectedOptionsPane?: boolean
  horizontalLayout?: boolean
}

const CustomComponentWrapper = styled.span`
  display: flex;
  ${RemoveItemButton} {
    margin-left: 10px;
  }
`

export class MultiCombobox extends React.Component<MultiComboboxProps<any, OptionKey>> implements Focusable {
  private handleAddOption = (optionKey: OptionKey) => {
    if (!this.props.value.includes(optionKey)) {
      const newValue = this.props.value.concat(optionKey)
      this.props.onUpdate(newValue)
    }
  }

  inputRef = React.createRef<Combobox>()

  public setFocus = () => {
    if (this.inputRef.current) {
      this.inputRef.current.setFocus()
    }
  }

  private handleDeleteOption = (optionKey: OptionKey) => {
    const newValue = this.props.value.filter(key => key !== optionKey)
    this.props.onUpdate(newValue)
  }

  private toggleOptionSelected = (optionKey: OptionKey, optionSelected: boolean, evt: React.MouseEvent<HTMLElement>) => {
    if (optionSelected) {
      this.handleDeleteOption(optionKey)
    } else {
      this.handleAddOption(optionKey)
    }

    evt.preventDefault()
    evt.stopPropagation()
  }

  private handleClearOptions = () => {
    this.props.onUpdate([])
  }

  private renderDropdownOption = (optionProps: OptionTemplateProps<any, OptionKey>) => {
    const optionSelected = this.props.value.includes(optionProps.optionKey)

    return (
      <DropdownOption {...optionProps}>
        <span>{optionProps.optionText}</span>
        <SelectButton
          dataTestId={optionSelected ? 'option-remove' : 'option-add'}
          type={TextButtonType.Primary}
          selected={optionSelected}
          canFocus={false}
          onClick={(evt: React.MouseEvent<HTMLElement>) => this.toggleOptionSelected(optionProps.optionKey, optionSelected, evt)}>
          {optionSelected ? iconSet.tickIcon : Locale.translate(Translation.TC_BUTTON_ADD)}
        </SelectButton>
      </DropdownOption>
    )
  }

  private renderSelectedOption = (optionProps: OptionTemplateProps<any, OptionKey>) => {
    const { disabled, readOnly, selectedOptionCustomComponent } = this.props
    const showRemoveButton = !disabled && !readOnly

    return (
      <SelectedOption {...optionProps}>
        <span>{optionProps.optionText}</span>
        <CustomComponentWrapper>
          {selectedOptionCustomComponent?.(optionProps.optionKey)}
          {showRemoveButton && (
            <RemoveItemButton
              dataTestId={`RemoveItemButton-${optionProps.optionText}`}
              onClick={() => this.handleDeleteOption(optionProps.optionKey)}>
              {iconSet.crossIcon}
            </RemoveItemButton>
          )}
        </CustomComponentWrapper>
      </SelectedOption>
    )
  }

  render() {
    const {
      className,
      value,
      disabled,
      dataTestId,
      readOnly,
      optionColorPalette,
      canClear,
      clearButtonLabel,
      placeholder,
      innerComboboxProps,
      showSelectedOptionsPane = true,
      withPortal,
      horizontalLayout,
      ...rest
    } = this.props

    return (
      <MultiComboboxWrapper className={className} data-test-id={dataTestId} horizontalLayout={horizontalLayout}>
        {!readOnly && (
          <InputContainer horizontalLayout={horizontalLayout}>
            <Combobox
              {...rest}
              value={null}
              withPortal={withPortal}
              disabled={disabled}
              onUpdate={this.handleAddOption}
              optionTemplate={this.renderDropdownOption}
              placeholder={value.length > 0 ? undefined : placeholder}
              dataTestId={dataTestId}
              ref={this.inputRef}
              {...innerComboboxProps}
            />
          </InputContainer>
        )}

        {value.length > 0 && showSelectedOptionsPane && (
          <>
            <SelectedListbox
              aria-label="Selected Options List"
              optionKeys={value}
              getOptionText={this.props.getOptionText}
              getOption={this.props.getOption}
              optionTemplate={this.renderSelectedOption}
              optionColorPalette={optionColorPalette}
              onOptionDoubleClick={disabled || readOnly ? noop : this.handleDeleteOption}
              marginTop={readOnly ? 0 : undefined}
              horizontalLayout={horizontalLayout}
            />

            {canClear && (
              <ClearButton disabled={value.length === 0 || readOnly || disabled} onClick={this.handleClearOptions}>
                {clearButtonLabel || Locale.translate(Translation.SC_BUTTON_CLEAR_ALL)}
              </ClearButton>
            )}
          </>
        )}
      </MultiComboboxWrapper>
    )
  }
}

export const MultiComboboxField = Field(MultiCombobox)
